import React from 'react'
import {
  CircleStackIcon,
  ClockIcon,
  Cog6ToothIcon,
  DocumentCheckIcon,
  ExclamationTriangleIcon,
  TableCellsIcon,
} from '@heroicons/react/24/outline'

import { Badge } from '../../../components/ui'
import type { useSyncContractContextToGoogleSheets } from '../api'
import type {
  ContractContext,
  ContractExtraction,
  ContractExtractionCitation,
  EditableApprovedSowData,
  GmailSowExtractResponse,
  GmailSowIntakeResponse,
} from '../types'
import { formatCurrency, formatDate, lifecycleValue } from './formatters'

const revenueProfileOptions = ['Fixed Fee', 'Manual', 'Time and Expense', 'Retainer'] as const

export type WorkflowStepState = 'complete' | 'active' | 'blocked' | 'pending'
export type WorkflowRunStatus = 'completed' | 'failed' | 'in_progress' | 'pending'

export type WorkflowStep = {
  id: string
  label: string
  description: string
  fromSystem: string
  toSystem: string
  state: WorkflowStepState
  detail: string
  timestamp: string
  failure?: string
}

export type WorkflowRun = {
  id: string
  label: string
  startedAt: string
  status: WorkflowRunStatus
  steps: WorkflowStep[]
  traces: WorkflowTraceEvent[]
  sowData: EditableApprovedSowData
}

export type OperatingWorkflowUpdate = {
  status: 'idle' | 'pending' | 'completed' | 'failed'
  message?: string
}

export type WorkflowTraceEvent = {
  id: string
  timestamp: string
  source: string
  target: string
  label: string
  status: WorkflowStepState
  detail: string
}

export type SheetsSyncMutation = ReturnType<typeof useSyncContractContextToGoogleSheets>
export type GmailSowAttachment = GmailSowIntakeResponse['messages'][number]['downloaded_attachments'][number]
export type PaymentScheduleRow = {
  timing: string
  amount: string
  description: string
}

export const editableFieldCitationMap: Partial<Record<keyof EditableApprovedSowData, string[]>> = {
  clientName: ['client_name'],
  workstream: ['workstream'],
  contractValue: ['contract_value', 'sow_contract_value'],
  contractLengthMonths: ['contract_length_months'],
  totalHours: ['total_hours'],
  numberOfFdes: ['number_of_fdes'],
  leadFde: ['lead_fde'],
  fdeAllocation: ['fde_allocations', 'number_of_fdes', 'lead_fde'],
  revenueProfile: ['revenue_profile'],
  expectedPaymentSchedule: ['expected_payment_schedule', 'invoice_timing', 'payment_terms', 'upfront_payment'],
  startDate: ['start_date'],
  endDate: ['end_date'],
  paymentTermsDays: ['payment_terms', 'invoice_timing'],
  notes: ['notes'],
}

export function findCitationForEditableField(
  citations: ContractExtractionCitation[],
  field: keyof EditableApprovedSowData,
): ContractExtractionCitation | undefined {
  const targets = editableFieldCitationMap[field] || []
  if (!targets.length) return undefined
  return citations.find(citation => targets.includes(citation.field))
}

export function getPdfSearchText(citation?: ContractExtractionCitation): string {
  const raw = citation?.search_text || citation?.snippet || ''
  return raw
    .replace(/^\.\.\./, '')
    .replace(/\.\.\.$/, '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 320)
}

export function getPdfHighlightContext(citation?: ContractExtractionCitation): string {
  return (citation?.snippet || '')
    .replace(/^\.\.\./, '')
    .replace(/\.\.\.$/, '')
    .replace(/\s+/g, ' ')
    .trim()
}

export const workflowStepVariants: Record<WorkflowStepState, React.ComponentProps<typeof Badge>['variant']> = {
  complete: 'success',
  active: 'info',
  blocked: 'danger',
  pending: 'secondary',
}

export const workflowStatusLabels: Record<WorkflowStepState, string> = {
  complete: 'Done',
  active: 'In Progress',
  blocked: 'Blocked',
  pending: 'Waiting',
}

export const runStatusVariants: Record<WorkflowRunStatus, React.ComponentProps<typeof Badge>['variant']> = {
  completed: 'success',
  failed: 'danger',
  in_progress: 'info',
  pending: 'secondary',
}

export const runStatusLabels: Record<WorkflowRunStatus, string> = {
  completed: 'Completed',
  failed: 'Failed',
  in_progress: 'In Progress',
  pending: 'Pending',
}

export function getWorkflowSystemIcon(step: WorkflowStep) {
  if (step.state === 'blocked') return ExclamationTriangleIcon
  if (step.id === 'sow-signature-received') return DocumentCheckIcon
  if (step.id === 'hubspot-updated') return CircleStackIcon
  if (step.id === 'revenue-spreadsheets-updated') return TableCellsIcon
  if (step.id === 'operating-updated') return Cog6ToothIcon
  return ClockIcon
}

export function buildWorkflowSteps(
  context: ContractContext | undefined,
  syncSheets: SheetsSyncMutation,
  latestSowEmail?: GmailSowIntakeResponse['messages'][number],
  operatingUpdate?: OperatingWorkflowUpdate,
): WorkflowStep[] {
  const contracts = context?.contracts || []
  const deals = context?.deals || []
  const sowStatus = lifecycleValue(context?.lifecycle || {}, 'sow')
  const sowComplete = sowStatus === 'signed' || contracts.length > 0
  const signatureReceived = sowComplete || Boolean(latestSowEmail)
  const hubspotComplete = deals.length > 0
  const sheetsComplete = Boolean(syncSheets.data)
  const sheetsActive = Boolean(syncSheets.isPending)
  const sheetsFailed = Boolean(syncSheets.isError)
  const operatingComplete = operatingUpdate?.status === 'completed'
  const operatingFailed = operatingUpdate?.status === 'failed'
  const operatingPending = operatingUpdate?.status === 'pending'

  return [
    {
      id: 'sow-signature-received',
      label: 'SOW Signature Received',
      description: 'Signed statement of work received and ready for extraction.',
      fromSystem: 'Signature Intake',
      toSystem: 'SOW Extraction',
      state: signatureReceived ? 'complete' : context ? 'blocked' : 'pending',
      detail: sowComplete
        ? `${contracts.length} signed SOW ${contracts.length === 1 ? 'document' : 'documents'} received`
        : latestSowEmail
          ? latestSowEmail.subject || 'Signed SOW email found'
        : context
          ? 'No signed SOW signature found for this client'
          : 'Waiting for signature email',
      timestamp: sowComplete ? 'Received now' : latestSowEmail?.date || (context ? 'Needs review' : 'Waiting'),
      failure: signatureReceived || !context ? undefined : 'Inbox search did not return a matching signed SOW.',
    },
    {
      id: 'hubspot-updated',
      label: 'HubSpot Updated',
      description: 'Deal record matched to contract value and lifecycle.',
      fromSystem: 'SOW Extraction',
      toSystem: 'HubSpot',
      state: hubspotComplete ? 'complete' : sowComplete ? 'active' : 'pending',
      detail: hubspotComplete
        ? `${deals.length} HubSpot ${deals.length === 1 ? 'deal' : 'deals'} matched`
        : sowComplete
          ? 'Ready to reconcile HubSpot deal data'
          : 'Waiting on SOW extraction',
      timestamp: hubspotComplete ? 'Matched now' : sowComplete ? 'Ready' : 'Waiting',
      failure: sowComplete && !hubspotComplete ? 'No matching HubSpot deal was found.' : undefined,
    },
    {
      id: 'revenue-spreadsheets-updated',
      label: 'Revenue Spreadsheets Updated',
      description: 'Revenue schedule pushed into the finance spreadsheet.',
      fromSystem: 'SOW Extraction',
      toSystem: 'Revenue Spreadsheet',
      state: sheetsComplete ? 'complete' : sheetsFailed ? 'blocked' : sheetsActive ? 'active' : sowComplete ? 'active' : 'pending',
      detail: sheetsComplete
        ? `${syncSheets.data?.updated_rows || 0} rows synced`
        : sheetsActive
          ? 'Syncing spreadsheet rows'
          : sowComplete
          ? 'Ready to sync to Google Sheets'
          : 'Waiting on SOW extraction',
      timestamp: sheetsComplete ? 'Synced now' : sheetsActive ? 'Syncing' : sowComplete ? 'Ready' : 'Waiting',
      failure: sheetsFailed ? 'Google Sheets sync failed. Check connector credentials and spreadsheet access.' : undefined,
    },
    {
      id: 'operating-updated',
      label: 'Operating Updated',
      description: 'Final operating view reflects the approved contract workflow.',
      fromSystem: 'SOW Extraction',
      toSystem: 'Operating',
      state: operatingComplete ? 'complete' : operatingFailed ? 'blocked' : operatingPending || sowComplete ? 'active' : 'pending',
      detail: operatingComplete
        ? operatingUpdate?.message || 'Operating workflow is current'
        : operatingPending
          ? 'Updating Operating from reviewed SOW data'
          : sowComplete
            ? 'Ready for Operating handoff'
            : 'Waiting on SOW extraction',
      timestamp: operatingComplete ? 'Updated now' : operatingPending ? 'Updating' : sowComplete ? 'Ready' : 'Waiting',
      failure: operatingFailed ? operatingUpdate?.message || 'Operating update failed.' : undefined,
    },
  ]
}

export function buildTraceEvents(steps: WorkflowStep[]): WorkflowTraceEvent[] {
  return steps.map((step, index) => ({
    id: `trace-${step.id}`,
    timestamp: step.timestamp,
    source: step.fromSystem,
    target: step.toSystem,
    label: step.label,
    status: step.state,
    detail: step.failure || step.detail || (index === 0 ? 'SOW intake started' : 'Waiting on previous handoff'),
  }))
}

export function formatFdeAllocations(extraction: ContractExtraction): string {
  const allocations = extraction.fde_allocations || []
  if (allocations.length === 0) return ''
  return allocations
    .map(item => {
      const label = item.name || item.role || 'FDE'
      const pieces = [item.allocation, item.role && item.role !== label ? item.role : undefined].filter(Boolean)
      return pieces.length ? `${label}: ${pieces.join(', ')}` : label
    })
    .join('\n')
}

export function parseMoneyText(value?: string | null): number | null {
  if (!value) return null
  const match = value.match(/\$?\s*(\d[\d,]*(?:\.\d+)?|\d+(?:\.\d+)?)([kKmM])?/)
  if (!match) return null
  let numeric = Number(match[1].replace(/,/g, ''))
  if (!Number.isFinite(numeric)) return null
  const suffix = match[2]?.toLowerCase()
  if (suffix === 'k') numeric *= 1000
  if (suffix === 'm') numeric *= 1000000
  return numeric
}

export function parseIsoDate(value?: string | null): Date | null {
  if (!value) return null
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (!match) return null
  const parsed = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])))
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

export function formatIsoDate(value: Date): string {
  return value.toISOString().slice(0, 10)
}

export function addMonths(value: Date, offset: number): Date {
  const next = new Date(value)
  next.setUTCMonth(value.getUTCMonth() + offset)
  return next
}

export function inferContractMonths(extraction: ContractExtraction): number | null {
  if (extraction.contract_length_months) return Math.max(1, Math.round(extraction.contract_length_months))
  const text = [
    extraction.expected_payment_schedule,
    extraction.invoice_timing,
    extraction.payment_terms,
    extraction.notes,
  ].filter(Boolean).join(' ')
  const monthMatch = text.match(/\b(\d{1,3})\s*(?:months?|mos?)\b/i)
  if (monthMatch?.[1]) return Math.max(1, Number(monthMatch[1]))
  const start = parseIsoDate(extraction.start_date)
  const end = parseIsoDate(extraction.end_date)
  if (!start || !end || end < start) return null
  return Math.max(1, (end.getUTCFullYear() - start.getUTCFullYear()) * 12 + end.getUTCMonth() - start.getUTCMonth() + 1)
}

export function inferMonthlyAmount(extraction: ContractExtraction, months: number | null): number | null {
  const text = [
    extraction.revenue_profile,
    extraction.expected_payment_schedule,
    extraction.invoice_timing,
    extraction.payment_terms,
    extraction.notes,
  ].filter(Boolean).join(' ')
  const patterns = [
    /(?<amount>\$?\s*\d[\d,]*(?:\.\d+)?[kKmM]?)\s*(?:\/|per\s+)?(?:month|mo|monthly)\b/i,
    /(?:monthly|month(?:ly)?\s+(?:fee|rate|payment)|per\s+month).*?(?<amount>\$?\s*\d[\d,]*(?:\.\d+)?[kKmM]?)/i,
  ]
  for (const pattern of patterns) {
    const match = text.match(pattern)
    const amountToken = match?.groups?.amount
    if (amountToken && !/[$,kKmM]/.test(amountToken)) continue
    const amount = parseMoneyText(amountToken)
    if (amount) return amount
  }
  const mentionsMonthly = /\b(month|monthly|per\s+mo|\/mo|retainer)\b/i.test(text)
  if (extraction.contract_value && months && mentionsMonthly) {
    const upfront = extraction.upfront_payment || 0
    return Math.max(0, extraction.contract_value - upfront) / months
  }
  return null
}

export function inferExpectedPaymentRows(extraction: ContractExtraction): PaymentScheduleRow[] {
  const start = parseIsoDate(extraction.start_date)
  const months = inferContractMonths(extraction)
  const monthlyAmount = inferMonthlyAmount(extraction, months)
  if (!start || !months || !monthlyAmount) return []

  const termsDays = Number(inferPaymentTermsDays(extraction)) || 30
  const termsLabel = `Net ${termsDays}`
  const rows: PaymentScheduleRow[] = []

  if (extraction.upfront_payment) {
    const due = new Date(start)
    due.setUTCDate(due.getUTCDate() + termsDays)
    rows.push({
      timing: formatIsoDate(start),
      amount: formatCurrency(extraction.upfront_payment, extraction.currency || 'USD'),
      description: `Upfront payment; due ${formatIsoDate(due)} (${termsLabel})`,
    })
  }

  for (let index = 0; index < months; index += 1) {
    const invoiceDate = addMonths(start, index)
    const due = new Date(invoiceDate)
    due.setUTCDate(due.getUTCDate() + termsDays)
    const monthLabel = new Intl.DateTimeFormat('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' }).format(invoiceDate)
    rows.push({
      timing: formatIsoDate(invoiceDate),
      amount: formatCurrency(monthlyAmount, extraction.currency || 'USD'),
      description: `Monthly payment for ${monthLabel}; due ${formatIsoDate(due)} (${termsLabel})`,
    })
  }

  return rows
}

export function formatExpectedPaymentSchedule(extraction: ContractExtraction): string {
  const inferredRows = inferExpectedPaymentRows(extraction)
  if (inferredRows.length > 0) return serializePaymentScheduleRows(inferredRows)

  const milestones = extraction.milestones || []
  if (milestones.length > 0) {
    return milestones
      .filter(milestone => milestone.amount || milestone.name || milestone.due_date)
      .map(milestone => {
        const pieces = [
          milestone.name || 'Milestone payment',
          milestone.due_date || milestone.estimated_date ? `due ${formatDate(milestone.due_date || milestone.estimated_date)}` : undefined,
          milestone.amount ? formatCurrency(milestone.amount, extraction.currency || 'USD') : undefined,
        ].filter(Boolean)
        return pieces.join(' - ')
      })
      .join('\n')
  }

  if (extraction.expected_payment_schedule) return extraction.expected_payment_schedule

  const monthly = extraction.revenue_schedule?.length ? extraction.revenue_schedule : extraction.monthly_revenue || []
  if (monthly.length > 0) {
    return monthly
      .filter(row => row.month || row.amount)
      .map(row => `${row.month || 'Month'} - ${formatCurrency(row.amount, extraction.currency || 'USD')}`)
      .join('\n')
  }

  const pieces = [
    extraction.payment_terms ? `Payment terms: ${extraction.payment_terms}` : undefined,
    extraction.invoice_timing ? `Invoice timing: ${extraction.invoice_timing}` : undefined,
    extraction.upfront_payment ? `Upfront payment: ${formatCurrency(extraction.upfront_payment, extraction.currency || 'USD')}` : undefined,
    extraction.upfront_credited_at_end ? 'Upfront payment credited at the end of the contract.' : undefined,
  ].filter(Boolean)
  return pieces.join('\n')
}

export function parsePaymentScheduleRows(value: string): PaymentScheduleRow[] {
  const lines = value
    .split('\n')
    .map(line => line.trim())
    .filter(Boolean)

  if (lines.length === 0) {
    return [{ timing: '', amount: '', description: '' }]
  }

  return lines.map(line => {
    const parts = line.split(/\s+-\s+/)
    if (parts.length >= 3) {
      return {
        timing: parts[0] || '',
        amount: parts[1] || '',
        description: parts.slice(2).join(' - '),
      }
    }
    if (parts.length === 2) {
      const firstLooksLikeAmount = /[$€£]|\d/.test(parts[0]) && !/\d{4}-\d{2}|due|monthly|quarter|annual|signing/i.test(parts[0])
      return firstLooksLikeAmount
        ? { timing: '', amount: parts[0], description: parts[1] }
        : { timing: parts[0], amount: parts[1], description: '' }
    }
    return { timing: '', amount: '', description: line }
  })
}

export function serializePaymentScheduleRows(rows: PaymentScheduleRow[]): string {
  return rows
    .map(row => [row.timing.trim(), row.amount.trim(), row.description.trim()].filter(Boolean).join(' - '))
    .filter(Boolean)
    .join('\n')
}

export function normalizeRevenueProfile(value?: string | null): string {
  const normalized = (value || '').trim().toLowerCase().replace(/[-_/]+/g, ' ')
  if (!normalized) return ''
  if (normalized.includes('time') || normalized === 't e' || normalized === 't&e') return 'Time and Expense'
  if (normalized.includes('retainer')) return 'Retainer'
  if (normalized.includes('manual')) return 'Manual'
  if (normalized.includes('fixed') || normalized.includes('fee') || normalized.includes('price')) return 'Fixed Fee'
  return revenueProfileOptions.find(option => option.toLowerCase() === normalized) || ''
}

export function inferPaymentTermsDays(extraction: ContractExtraction): string {
  const paymentText = [
    extraction.payment_terms,
    extraction.invoice_timing,
    extraction.expected_payment_schedule,
    extraction.notes,
  ]
    .filter(Boolean)
    .join(' ')
  const netMatch = paymentText.match(/\bnet\s*(\d{1,3})\b/i)
  if (netMatch?.[1]) return netMatch[1]
  const dayMatch = paymentText.match(/\b(\d{1,3})\s*days?\b/i)
  if (dayMatch?.[1]) return dayMatch[1]
  return '30'
}

export function buildContextSowData(context: ContractContext | undefined): EditableApprovedSowData {
  const firstContract = context?.contracts?.[0]
  const extraction = firstContract?.extraction || {}
  const firstDeal = context?.deals?.[0]
  const startDate = extraction.start_date || ''
  const endDate = extraction.end_date || ''
  const start = startDate ? new Date(startDate) : null
  const end = endDate ? new Date(endDate) : null
  const monthDiff =
    start && end && !Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime())
      ? Math.max(1, Math.round((end.getFullYear() - start.getFullYear()) * 12 + end.getMonth() - start.getMonth() + 1))
      : ''

  return {
    contractId: '',
    clientName: context?.client_name || 'Automatic SOW watcher',
    sowFile: firstContract?.file_name || 'Waiting for SOW',
    workstream: extraction.workstream || firstDeal?.deal_name || '',
    contractStatus: 'Signed',
    contractValue: extraction.contract_value ? String(extraction.contract_value) : '',
    contractLengthMonths: extraction.contract_length_months
      ? String(extraction.contract_length_months)
      : monthDiff
        ? String(monthDiff)
        : '',
    totalHours: extraction.total_hours ? String(extraction.total_hours) : '',
    numberOfFdes: extraction.number_of_fdes ? String(extraction.number_of_fdes) : '',
    leadFde: extraction.lead_fde || '',
    leadFdePersonId: '',
    fdeAllocation: formatFdeAllocations(extraction),
    fdeAllocationRows: extraction.fde_allocations?.length ? JSON.stringify(extraction.fde_allocations) : '',
    revenueProfile: normalizeRevenueProfile(extraction.revenue_profile),
    expectedPaymentSchedule: formatExpectedPaymentSchedule(extraction),
    startDate,
    endDate,
    closeDate: firstDeal?.close_date || startDate,
    paymentTermsDays: inferPaymentTermsDays(extraction),
    bdLead: '',
    hubspotStage: firstDeal?.stage || 'Waiting',
    notes: extraction.notes || 'Waiting for the next SOW to hit the inbox.',
  }
}

export function buildApprovedSowDataFromExtraction(result: GmailSowExtractResponse): EditableApprovedSowData {
  const extraction = result.extraction || {}
  const fallback = buildContextSowData(result.context)
  return {
    ...fallback,
    clientName: extraction.client_name || result.context.client_name || fallback.clientName,
    sowFile: result.file_name,
    workstream: extraction.workstream || extraction.matched_deal || fallback.workstream,
    contractValue: extraction.contract_value ? String(extraction.contract_value) : fallback.contractValue,
    contractLengthMonths: extraction.contract_length_months
      ? String(extraction.contract_length_months)
      : fallback.contractLengthMonths,
    totalHours: extraction.total_hours ? String(extraction.total_hours) : fallback.totalHours,
    numberOfFdes: extraction.number_of_fdes ? String(extraction.number_of_fdes) : fallback.numberOfFdes,
    leadFde: extraction.lead_fde || fallback.leadFde,
    fdeAllocation: formatFdeAllocations(extraction) || fallback.fdeAllocation,
    fdeAllocationRows: extraction.fde_allocations?.length
      ? JSON.stringify(extraction.fde_allocations)
      : fallback.fdeAllocationRows || '',
    revenueProfile: normalizeRevenueProfile(extraction.revenue_profile) || fallback.revenueProfile,
    expectedPaymentSchedule: formatExpectedPaymentSchedule(extraction) || fallback.expectedPaymentSchedule,
    startDate: extraction.start_date || fallback.startDate,
    endDate: extraction.end_date || fallback.endDate,
    closeDate: fallback.closeDate || extraction.start_date || '',
    paymentTermsDays: inferPaymentTermsDays(extraction),
    notes: extraction.notes || fallback.notes,
  }
}

export function numberOrNull(value: string): number | null {
  const cleaned = value.replace(/[$,]/g, '').trim()
  if (!cleaned) return null
  const numeric = Number(cleaned)
  return Number.isFinite(numeric) ? numeric : null
}

export function buildContextFromApprovedSow(data: EditableApprovedSowData): ContractContext {
  const contractValue = numberOrNull(data.contractValue)
  const contractLengthMonths = numberOrNull(data.contractLengthMonths)
  const totalHours = numberOrNull(data.totalHours)
  const numberOfFdes = numberOrNull(data.numberOfFdes)
  return {
    client_name: data.clientName || 'Approved SOW',
    contracts: [
      {
        file_name: data.sowFile || 'Approved SOW.pdf',
        mime_type: 'application/pdf',
        extraction: {
          client_name: data.clientName || null,
          contract_value: contractValue,
          sow_contract_value: contractValue,
          currency: 'USD',
          start_date: data.startDate || null,
          end_date: data.endDate || null,
          contract_length_months: contractLengthMonths,
          total_hours: totalHours,
          number_of_fdes: numberOfFdes,
          lead_fde: data.leadFde || null,
          fde_allocations: data.fdeAllocation
            .split('\n')
            .map(line => line.trim())
            .filter(Boolean)
            .map(line => ({ name: line.split(':')[0]?.trim() || null, allocation: line.split(':').slice(1).join(':').trim() || null })),
          revenue_profile: data.revenueProfile || null,
          payment_terms: data.paymentTermsDays ? `Net ${data.paymentTermsDays}` : null,
          expected_payment_schedule: data.expectedPaymentSchedule || null,
          value_source: 'sow',
          matched_deal: null,
          notes: data.notes || null,
          revenue_schedule: [],
          monthly_revenue: [],
          milestones: [],
        },
      },
    ],
    deals: [],
    lifecycle: {
      stage: data.hubspotStage || 'negotiation',
      sow: 'signed',
      msa: 'unknown',
      payment: 'unknown',
    },
    summary: `Approved SOW for ${data.clientName || 'client'}: ${data.numberOfFdes || '-'} FDEs, lead ${data.leadFde || 'unassigned'}, ${data.contractValue || '-'} contract value.`,
  }
}

export function buildWorkflowRuns(context: ContractContext | undefined, currentSteps: WorkflowStep[]): WorkflowRun[] {
  if (!context) return []

  const failedCurrentStep = currentSteps.find(step => step.state === 'blocked')
  const hasActiveStep = currentSteps.some(step => step.state === 'active')
  const currentStatus: WorkflowRunStatus = failedCurrentStep
    ? 'failed'
    : hasActiveStep
      ? 'in_progress'
      : currentSteps.every(step => step.state === 'complete')
        ? 'completed'
        : 'pending'

  const contextSowData = buildContextSowData(context)

  return [
    {
      id: 'current-run',
      label: `${context.client_name} current run`,
      startedAt: currentSteps[0]?.timestamp || 'Current search',
      status: currentStatus,
      steps: currentSteps,
      traces: buildTraceEvents(currentSteps),
      sowData: contextSowData,
    },
  ]
}
